import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  NgZone,
  ViewChild,
  inject
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { chartColors, chartPalette } from './chart-theme';

export interface DonutChartItem {
  name: string;
  value: number;
  color?: string;
  rank?: number;
  contactId?: string;
  invoiceCount?: number;
  percentage?: number;
  grossProfit?: number;
  grossProfitMarginPercent?: number | null;
  totalCompanyRevenue?: number;
  useDistinctColor?: boolean;
}

export interface DonutChartSliceEvent {
  dataIndex: number;
  name: string;
  nativeEvent: MouseEvent;
}

interface DonutHoverTooltip {
  x: number;
  y: number;
  name: string;
  value: string;
  percentage: string;
  invoiceCount: string;
  grossProfit: string;
  grossProfitMargin: string;
}

@Component({
  selector: 'app-evertime-donut-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full min-h-[220px] overflow-visible' },
  templateUrl: './evertime-donut-chart.component.html',
  styleUrl: './evertime-donut-chart.component.scss',
})
export class EvertimeDonutChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: DonutChartItem[] = [];
  @Output() sliceClick = new EventEmitter<DonutChartSliceEvent>();
  @Output() sliceContextMenu = new EventEmitter<DonutChartSliceEvent>();

  @ViewChild('chartContainer') private chartContainer?: ElementRef<HTMLElement>;

  options: EChartsOption | null = null;
  legendItems: Array<{ name: string; color: string }> = [];
  hoverTooltip: DonutHoverTooltip | null = null;

  private hasBuiltOptions = false;
  private totalValue = 1;
  private renderedRows: DonutChartItem[] = [];
  private readonly valueFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  private themeSub?: Subscription;
  private chartInstance?: {
    resize: () => void;
    clear?: () => void;
    dispatchAction?: (payload: Record<string, unknown>) => void;
    setOption?: (option: EChartsOption) => void;
    on?: (eventName: string, handler: (params: any) => void) => void;
    off?: (eventName: string, handler: (params: any) => void) => void;
  };
  private resizeObserver?: ResizeObserver;
  private readonly themeService = inject(ThemeService);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetector = inject(ChangeDetectorRef);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }

    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(instance: {
    resize: () => void;
    clear?: () => void;
    dispatchAction?: (payload: Record<string, unknown>) => void;
    setOption?: (option: EChartsOption) => void;
    on?: (eventName: string, handler: (params: any) => void) => void;
    off?: (eventName: string, handler: (params: any) => void) => void;
  }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  onChartMouseOut(): void {
    this.hoverTooltip = null;
    this.chartInstance?.dispatchAction?.({
      type: 'downplay',
      seriesIndex: 0,
    });
  }

  onContainerMouseMove(event: MouseEvent): void {
    const element = this.chartContainer?.nativeElement;
    if (!element || this.renderedRows.length === 0) return;

    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const centerX = rect.width * 0.5;
    const centerY = rect.height * 0.48;
    const baseRadius = Math.min(rect.width, rect.height) / 2;
    const distance = Math.hypot(x - centerX, y - centerY);
    if (distance < baseRadius * 0.28 || distance > baseRadius * 0.9) {
      this.hoverTooltip = null;
      return;
    }

    const angle = (Math.atan2(x - centerX, -(y - centerY)) + Math.PI * 2) % (Math.PI * 2);
    const target = angle / (Math.PI * 2) * this.totalValue;
    let runningTotal = 0;
    const item = this.renderedRows.find(row => {
      runningTotal += row.value;
      return target <= runningTotal;
    });
    if (!item) {
      this.hoverTooltip = null;
      return;
    }

    const pct = item.percentage == null
      ? ((item.value / this.totalValue) * 100).toFixed(2)
      : Number(item.percentage).toFixed(2);
    this.hoverTooltip = {
      x: Math.min(x + 14, Math.max(8, rect.width - 248)),
      y: Math.min(y + 14, Math.max(8, rect.height - 140)),
      name: item.name,
      value: this.valueFormatter.format(item.value),
      percentage: `${pct}%`,
      invoiceCount: item.invoiceCount == null ? '—' : String(item.invoiceCount),
      grossProfit: item.grossProfit == null ? '—' : this.valueFormatter.format(item.grossProfit),
      grossProfitMargin: item.grossProfitMarginPercent == null
        ? '—'
        : `${Number(item.grossProfitMarginPercent).toFixed(2)}%`,
    };
  }

  onChartClick(params: any): void {
    if (typeof params?.dataIndex !== 'number') {
      return;
    }

    const nativeEvent = params.event?.event ?? params.event;
    if (!nativeEvent) {
      return;
    }

    this.sliceClick.emit({
      dataIndex: params.dataIndex,
      name: String(params.name ?? ''),
      nativeEvent
    });

    this.chartInstance?.dispatchAction?.({
      type: 'downplay',
      seriesIndex: 0,
      dataIndex: params.dataIndex,
    });
  }

  onChartContextMenu(params: any): void {
    if (typeof params?.dataIndex !== 'number') {
      return;
    }

    const nativeEvent = params.event?.event ?? params.event;
    if (!nativeEvent) {
      return;
    }

    nativeEvent.preventDefault?.();
    this.sliceContextMenu.emit({
      dataIndex: params.dataIndex,
      name: String(params.name ?? ''),
      nativeEvent
    });
  }

  get legendRows(): Array<Array<{ name: string; color: string }>> {
    const rows: Array<Array<{ name: string; color: string }>> = [];
    for (let i = 0; i < this.legendItems.length; i += 2) {
      rows.push(this.legendItems.slice(i, i + 2));
    }
    return rows;
  }

  private buildOptions(): void {
    const rows = (this.data ?? [])
      .map(item => ({
        ...item,
        value: Number(item.value) || 0
      }))
      .filter(item => item.value > 0);

    if (rows.length === 0) {
      this.hasBuiltOptions = false;
      this.options = null;
      this.legendItems = [];
      this.renderedRows = [];
      this.hoverTooltip = null;
      this.chartInstance?.clear?.();
      this.chartInstance = undefined;
      return;
    }

    const c = chartColors();
    const palette = chartPalette();
    const isDark = document.documentElement.classList.contains('dark');
    const tooltipBg = isDark ? (c.chartSurface || '#1E2835') : '#ffffff';
    const tooltipText = isDark ? (c.textPrimary || '#F5F7FA') : '#0f172a';
    const tooltipBorder = isDark ? (c.cardBorder || '#2B3849') : '#dbe4ee';
    this.totalValue = rows.reduce((sum, item) => sum + item.value, 0) || 1;
    this.renderedRows = rows;
    const colorFor = (item: DonutChartItem, index: number): string => {
      if (item.name.trim().toLowerCase() === 'others') {
        return item.color ?? '#94a3b8';
      }
      if (item.useDistinctColor) {
        return palette[index % palette.length];
      }

      const normalizedName = item.name.toLowerCase();
      if (normalizedName.includes('permanent') || normalizedName.includes('perm')) {
        return c.accent;
      }
      if (normalizedName.includes('contract')) {
        return palette[0] ?? c.primary;
      }
      if (normalizedName.includes('temporary') || normalizedName.includes('temp')) {
        return c.warning;
      }

      return item.color ?? palette[index % palette.length];
    };

    this.legendItems = rows.map((item, index) => ({
      name: item.name,
      color: colorFor(item, index),
    }));

    const nextOptions: EChartsOption = {
      color: this.legendItems.map(item => item.color),
      tooltip: {
        trigger: 'item',
        confine: true,
        position: (point: number[], _params: any, _element: any, _rect: any, size: any) => {
          const gap = 12;
          const padding = 8;
          const contentWidth = Number(size?.contentSize?.[0] ?? 0);
          const contentHeight = Number(size?.contentSize?.[1] ?? 0);
          const viewWidth = Number(size?.viewSize?.[0] ?? 0);
          const viewHeight = Number(size?.viewSize?.[1] ?? 0);
          const rightX = point[0] + gap;
          const x = rightX + contentWidth <= viewWidth - padding
            ? rightX
            : Math.max(padding, point[0] - contentWidth - gap);
          const y = Math.max(
            padding,
            Math.min(point[1] - contentHeight / 2, viewHeight - contentHeight - padding),
          );
          return [x, y];
        },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        formatter: (params: any) => {
          const row = rows[params?.dataIndex];
          if (!row) return '';
          const percentage = row.percentage == null
            ? (row.value / this.totalValue) * 100
            : Number(row.percentage);
          const stat = (label: string, value: string) => `
            <div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:4px;">
              <span style="color:${c.textSecondary};">${label}</span>
              <strong style="color:${c.textPrimary};">${value}</strong>
            </div>`;
          return `<div style="font-family:Inter,sans-serif;min-width:220px;">
            <div style="font-weight:800;color:${c.textPrimary};margin-bottom:8px;">${row.name}</div>
            ${stat('Revenue', this.valueFormatter.format(row.value))}
            ${stat('Revenue percentage', `${percentage.toFixed(2)}%`)}
            ${stat('Gross profit', row.grossProfit == null ? '—' : this.valueFormatter.format(row.grossProfit))}
            ${stat('GP margin', row.grossProfitMarginPercent == null ? '—' : `${Number(row.grossProfitMarginPercent).toFixed(2)}%`)}
          </div>`;
        }
      },
      legend: {
        show: false,
        type: 'plain',
      },
      animation: false,
      series: [{
        type: 'pie',
        radius: ['28%', '90%'],
        center: ['50%', '48%'],
        selectedMode: false,
        selectedOffset: 0,
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: {
          borderColor: c.cardBg,
          borderWidth: 2
        },
        emphasis: {
          scale: false,
          label: { show: false },
          itemStyle: {
            opacity: 0.82,
            shadowBlur: 0,
          }
        },
        data: rows.map((item, index) => ({
          ...item,
          name: item.name,
          value: item.value,
          selected: false,
          itemStyle: {
            color: colorFor(item, index)
          }
        }))
      }]
    };

    if (this.hasBuiltOptions && this.chartInstance?.setOption) {
      // Merge the update into the live instance instead of replacing the
      // `[options]` binding. ngx-echarts always applies input changes with
      // notMerge:true, which tears down and rebuilds the pie's graphic
      // elements; ECharts' item-triggered tooltip tracks hover state by
      // element identity, so a full rebuild right after a hover leaves it
      // desynced and the next hover silently fails to show a tooltip.
      this.chartInstance.setOption(nextOptions);
    } else {
      this.hasBuiltOptions = true;
      this.options = nextOptions;
    }

    this.hoverTooltip = null;
  }

  private updateHoverTooltip(params: any): void {
    if (params?.seriesType !== 'pie' || typeof params?.value === 'undefined') {
      return;
    }

    const nativeEvent = params.event?.event ?? params.event;
    const value = Number(params.value) || 0;
    const source = params.data as DonutChartItem;
    const pct = source.percentage == null
      ? (this.totalValue ? ((value / this.totalValue) * 100).toFixed(2) : '0.00')
      : Number(source.percentage).toFixed(2);

    const chartRect = this.chartContainer?.nativeElement.getBoundingClientRect();
    if (!chartRect) return;
    const localX = Number.isFinite(Number(nativeEvent?.clientX))
      ? Number(nativeEvent.clientX) - chartRect.left
      : Number(nativeEvent?.offsetX ?? 0);
    const localY = Number.isFinite(Number(nativeEvent?.clientY))
      ? Number(nativeEvent.clientY) - chartRect.top
      : Number(nativeEvent?.offsetY ?? 0);

    this.hoverTooltip = {
      x: Math.min(localX + 14, Math.max(8, chartRect.width - 248)),
      y: Math.min(localY + 14, Math.max(8, chartRect.height - 140)),
      name: String(params.name ?? ''),
      value: this.valueFormatter.format(value),
      percentage: `${pct}%`,
      invoiceCount: source.invoiceCount == null ? '—' : String(source.invoiceCount),
      grossProfit: source.grossProfit == null
        ? '—'
        : this.valueFormatter.format(source.grossProfit),
      grossProfitMargin: source.grossProfitMarginPercent == null
        ? '—'
        : `${Number(source.grossProfitMarginPercent).toFixed(2)}%`,
    };
  }

  private readonly onInstanceSliceHover = (params: any): void => {
    this.showCanvasStats(params);
    this.ngZone.run(() => {
      this.updateHoverTooltip(params);
      this.changeDetector.detectChanges();
    });
  };

  private readonly onInstanceMouseOut = (): void => {
    this.hideCanvasStats();
    this.ngZone.run(() => {
      this.onChartMouseOut();
      this.changeDetector.detectChanges();
    });
  };

  private unbindChartHoverEvents(): void {
    this.chartInstance?.off?.('mouseover', this.onInstanceSliceHover);
    this.chartInstance?.off?.('mousemove', this.onInstanceSliceHover);
    this.chartInstance?.off?.('globalout', this.onInstanceMouseOut);
  }

  private showCanvasStats(params: any): void {
    if (params?.seriesType !== 'pie') return;
    const source = (params.data ?? {}) as DonutChartItem;
    const value = Number(source.value ?? params.value ?? 0);
    const percentage = source.percentage == null
      ? (this.totalValue > 0 ? (value / this.totalValue) * 100 : 0)
      : Number(source.percentage);
    const c = chartColors();
    const text = [
      `{name|${source.name ?? params.name ?? '—'}}`,
      `{label|Revenue}{value|${this.valueFormatter.format(value)}}`,
      `{label|Revenue percentage}{value|${percentage.toFixed(2)}%}`,
      `{label|Invoice count}{value|${source.invoiceCount ?? '—'}}`,
      `{label|Gross profit}{value|${source.grossProfit == null ? '—' : this.valueFormatter.format(source.grossProfit)}}`,
      `{label|GP margin}{value|${source.grossProfitMarginPercent == null ? '—' : `${Number(source.grossProfitMarginPercent).toFixed(2)}%`}}`,
    ].join('\n');

    this.chartInstance?.setOption?.({
      graphic: [{
        id: 'revenue-concentration-stats',
        type: 'text',
        right: 8,
        top: 8,
        z: 100,
        silent: true,
        style: {
          text,
          fill: c.textSecondary || '#AAB6C5',
          backgroundColor: c.chartSurface || '#1E2835',
          borderColor: c.cardBorder || '#334155',
          borderWidth: 1,
          borderRadius: 8,
          padding: [10, 12],
          lineHeight: 17,
          rich: {
            name: {
              width: 218,
              padding: [0, 0, 7, 0],
              fill: c.textPrimary || '#F5F7FA',
              fontSize: 12,
              fontWeight: 800,
            },
            label: {
              width: 120,
              fill: c.textSecondary || '#AAB6C5',
              fontSize: 10,
              fontWeight: 600,
            },
            value: {
              width: 98,
              align: 'right',
              fill: c.textPrimary || '#F5F7FA',
              fontSize: 10,
              fontWeight: 800,
            },
          },
        },
      }],
    } as EChartsOption);
  }

  private hideCanvasStats(): void {
    this.chartInstance?.setOption?.({
      graphic: [{
        id: 'revenue-concentration-stats',
        $action: 'remove',
      }],
    } as EChartsOption);
  }

}
